import { ProductCtaType } from '@today/types'
import { Button, Checkbox } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText, SelectInput, TextArea, TextInput } from './product-fields'
import type { ProductFormValues, ProductOption, Translate } from './product-types'

type TextField = Exclude<keyof ProductFormValues, 'featured'>

/**
 * Props for {@link ProductFormView}. Client rules and server-error mapping live
 * in the page hook; this view only renders their results.
 *
 * @property locales - The tenant's enabled locales for the default-locale select; empty renders a text input.
 * @property errors - Translated field messages by field name (`cta` for a server CTA refusal).
 * @property readOnly - True without `product.write`: every input disabled, no Save.
 */
export interface ProductFormViewProps {
	t: Translate
	mode: 'create' | 'edit'
	values: ProductFormValues
	errors: Record<string, string>
	formError: string | null
	saved: boolean
	locales: string[]
	categories: ProductOption[]
	readOnly: boolean
	isBusy: boolean
	onChange: (field: keyof ProductFormValues, value: string | boolean) => void
	onSubmit: () => void
}

function CtaFields({ props, slot }: { props: ProductFormViewProps; slot: 'Primary' | 'Secondary' }): ReactElement {
	const { t, values, errors, readOnly, onChange } = props
	const typeField = `cta${slot}Type` as const
	const targetField = `cta${slot}Target` as const
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className="text-ds-sm font-medium text-ds-text">{t(`cta.${slot.toLowerCase()}`)}</legend>
			<SelectInput
				label={t('cta.type')}
				value={values[typeField]}
				emptyLabel={t('cta.none')}
				options={Object.values(ProductCtaType).map((id) => ({ id, label: t(`cta.types.${id}`) }))}
				disabled={readOnly}
				onChange={(v) => onChange(typeField, v)}
			/>
			{values[typeField] ? (
				<TextInput
					label={t('cta.target')}
					value={values[targetField]}
					error={errors[targetField]}
					disabled={readOnly}
					onChange={(v) => onChange(targetField, v)}
				/>
			) : null}
		</fieldset>
	)
}

function DefaultLocaleField({ props }: { props: ProductFormViewProps }): ReactElement {
	const { t, values, errors, readOnly, onChange } = props
	const error = errors.defaultLocale
	return props.locales.length > 0 ? (
		<SelectInput
			label={t('form.defaultLocale')}
			value={values.defaultLocale}
			options={props.locales.map((l) => ({ id: l, label: l }))}
			error={error}
			disabled={readOnly}
			onChange={(v) => onChange('defaultLocale', v)}
		/>
	) : (
		<TextInput
			label={t('form.defaultLocale')}
			value={values.defaultLocale}
			error={error}
			disabled={readOnly}
			onChange={(v) => onChange('defaultLocale', v)}
		/>
	)
}

function BaseFields({ props }: { props: ProductFormViewProps }): ReactElement {
	const { t, values, errors, readOnly, onChange } = props
	const text = (field: TextField, label: string) => (
		<TextInput
			label={t(label)}
			value={values[field]}
			error={errors[field]}
			disabled={readOnly}
			onChange={(v) => onChange(field, v)}
		/>
	)
	return (
		<>
			{props.mode === 'create' ? (
				text('code', 'form.code')
			) : (
				<p className="text-ds-sm text-ds-text">
					<span className="font-medium">{t('form.code')}</span> <code>{values.code}</code>
				</p>
			)}
			{text('name', 'form.name')}
			<DefaultLocaleField props={props} />
			<SelectInput
				label={t('form.category')}
				value={values.categoryId}
				emptyLabel={t('form.none')}
				options={props.categories}
				error={errors.categoryId}
				disabled={readOnly}
				onChange={(v) => onChange('categoryId', v)}
			/>
			<Checkbox isSelected={values.featured} isDisabled={readOnly} onChange={(v) => onChange('featured', v)}>
				{t('form.featured')}
			</Checkbox>
			{text('icon', 'form.icon')}
		</>
	)
}

/** The product base fields, CTAs and partner-attributes JSON editor (organism). */
export function ProductFormView(props: ProductFormViewProps): ReactElement {
	const { t, values, errors, readOnly } = props
	return (
		<form
			aria-label={t(props.mode === 'create' ? 'form.titleNew' : 'form.titleEdit')}
			className="flex flex-col gap-3"
			onSubmit={(e) => {
				e.preventDefault()
				props.onSubmit()
			}}
		>
			<h2 className="text-ds-lg font-semibold text-ds-text">
				{t(props.mode === 'create' ? 'form.titleNew' : 'form.titleEdit')}
			</h2>
			<BaseFields props={props} />
			<TextArea
				label={t('form.partnerAttributes')}
				description={t('form.partnerAttributesHint')}
				value={values.partnerAttributes}
				error={errors.partnerAttributes}
				disabled={readOnly}
				onChange={(v) => props.onChange('partnerAttributes', v)}
			/>
			<ErrorText message={errors.cta} />
			<CtaFields props={props} slot="Primary" />
			<CtaFields props={props} slot="Secondary" />
			<ErrorText message={props.formError} />
			{props.saved ? <p role="status">{t('form.saved')}</p> : null}
			{readOnly ? null : (
				<Button type="submit" isDisabled={props.isBusy}>
					{t(props.mode === 'create' ? 'form.create' : 'form.save')}
				</Button>
			)}
		</form>
	)
}
